// Copyright Abridged, Inc. 2023,2024. All Rights Reserved.
// Node module: @collabland/sdk
// This file is licensed under the MIT License.
// License text available at https://opensource.org/license/mit/

import {inject, Provider} from '@loopback/core';
import {getService, RestDataSource} from '../datasources/index.js';
import {TelefrensCopyTradeAvailabilityResponse} from '../models/telefrens-copy-trade-availability-response.model.js';
import {TelefrensCopyTradingGetQuoteRequest} from '../models/telefrens-copy-trading-get-quote-request.model.js';
import {TelefrensCopyTradingSubmitRequest} from '../models/telefrens-copy-trading-submit-request.model.js';
import {TelefrensCopyTradingSupportedTokensResponse} from '../models/telefrens-copy-trading-supported-tokens-response.model.js';
import {TelefrensCopyTradingValidateRequest} from '../models/telefrens-copy-trading-validate-request.model.js';
import {TelefrensCopyTradingValidateResponse} from '../models/telefrens-copy-trading-validate-response.model.js';
import {LiFiWidgetSubmitRequest} from '../models/li-fi-widget-submit-request.model.js';
import {LiFiWidgetWaitTxRequest} from '../models/li-fi-widget-wait-tx-request.model.js';
import {GetInviterResponse} from '../models/get-inviter-response.model.js';
import {ClaimResponse} from '../models/claim-response.model.js';

/**
 * The service interface is generated from OpenAPI spec with operations tagged
 * by TelefrensWebappController.
 */
export interface TelefrensWebappService {
  /**
   * @param limit
   * @returns OK
   */
  getCopyTradingActivityLinks(
    limit?: number,
  ): Promise<TelefrensCopyTradeAvailabilityResponse>;

  /**
   * @returns OK
   */
  checkCopyTradingAvailability(): Promise<TelefrensCopyTradeAvailabilityResponse>;

  /**
   * @param _requestBody
   * @returns OK
   */
  getCopyTradingQuote(
    _requestBody: TelefrensCopyTradingGetQuoteRequest,
  ): Promise<{}>;

  /**
   * @param _requestBody
   */
  getCopyTradingParam(
    _requestBody: TelefrensCopyTradingSubmitRequest,
  ): Promise<unknown>;

  /**
   * @returns OK
   */
  getSupportedTokensCopyTrading(): Promise<TelefrensCopyTradingSupportedTokensResponse>;

  /**
   * @param subjectUserId
   * @returns OK
   */
  getCopyTradingLinks(
    subjectUserId: number,
  ): Promise<TelefrensCopyTradeAvailabilityResponse>;

  /**
   * @param limit
   * @returns OK
   */
  getCopyTradingTrendingLinks(
    limit?: number,
  ): Promise<TelefrensCopyTradeAvailabilityResponse>;

  /**
   * @param disable_notifications
   * @param _requestBody
   * @returns OK
   */
  validateCreateCopyTrading(
    disable_notifications: boolean | undefined,
    _requestBody: TelefrensCopyTradingValidateRequest,
  ): Promise<TelefrensCopyTradingValidateResponse>;

  /**
   * @param _requestBody
   */
  submitLiFiTxForConfirmation(
    _requestBody: LiFiWidgetSubmitRequest,
  ): Promise<unknown>;

  /**
   * @param _requestBody
   */
  waitForLifiTransaction(
    _requestBody: LiFiWidgetWaitTxRequest,
  ): Promise<unknown>;

  /**
   * @param chainId
   */
  getMySmartAccount(chainId?: number): Promise<unknown>;

  /**
   */
  fundUserSmartAccount(): Promise<unknown>;

  /**
   */
  hasFundInPkp(): Promise<unknown>;

  /**
   * @param inviteCode
   * @returns OK
   */
  getInviter(inviteCode: string): Promise<GetInviterResponse>;

  /**
   * @returns OK
   */
  claimTfToken(): Promise<ClaimResponse>;

  /**
   * @param initData
   * @param chainId
   */
  webAppLogin(initData?: string, chainId?: number): Promise<unknown>;
}

export class TelefrensWebappServiceProvider
  implements Provider<TelefrensWebappService>
{
  constructor(
    // rest must match the name property in the datasource json file
    @inject('datasources.rest')
    protected dataSource: RestDataSource = new RestDataSource(),
  ) {}

  async value(): Promise<TelefrensWebappService> {
    const service = await getService<{
      apis: {TelefrensWebappController: TelefrensWebappService};
    }>(this.dataSource);
    return service.apis['TelefrensWebappController'];
  }
}
